import { useState, useRef, useEffect, useCallback } from "react";
import CustomSelectAddOnsStyles from "./CustomSelectAddOns.module.css";
import { AddOnProps } from "../../types/ComponentProps";

type CustomSelectAddOnsProps = {
  placeholder: string;
  onSelect: (option: AddOnProps) => void;
  options: AddOnProps[];
  setCreateProductForm?: React.Dispatch<React.SetStateAction<any>>;
  id?: string;
};

const CustomSelectAddOns: React.FC<CustomSelectAddOnsProps> = ({
  placeholder,
  onSelect,
  options,
  id,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);

  const listboxId = id ? `${id}-listbox` : "addons-select-listbox";
  const hasOptions = options && options.length > 0;

  const handleSelectOption = (option: AddOnProps) => {
    setIsOpen(false);
    setActiveIndex(-1);
    if (onSelect) {
      onSelect(option);
    }
    buttonRef.current?.focus();
  };

  const toggleDropdown = () => {
    if (!hasOptions) return;
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setActiveIndex(0);
    }
  };

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  // Scroll active option into view
  useEffect(() => {
    if (isOpen && activeIndex >= 0 && optionRefs.current[activeIndex]) {
      optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!hasOptions) return;

    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        if (isOpen && activeIndex >= 0) {
          handleSelectOption(options[activeIndex]);
        } else {
          toggleDropdown();
        }
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setActiveIndex(0);
        } else {
          setActiveIndex((prev) => Math.min(prev + 1, options.length - 1));
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (isOpen) {
          setActiveIndex((prev) => Math.max(prev - 1, 0));
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setActiveIndex(-1);
        buttonRef.current?.focus();
        break;
      case "Tab":
        setIsOpen(false);
        setActiveIndex(-1);
        break;
      case "Home":
        if (isOpen) {
          e.preventDefault();
          setActiveIndex(0);
        }
        break;
      case "End":
        if (isOpen) {
          e.preventDefault();
          setActiveIndex(options.length - 1);
        }
        break;
    }
  };

  return (
    <div className={CustomSelectAddOnsStyles.customSelect} ref={dropdownRef}>
      <button
        type="button"
        ref={buttonRef}
        className={CustomSelectAddOnsStyles.selectDisplay}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={hasOptions ? listboxId : undefined}
        aria-activedescendant={isOpen && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
        disabled={!hasOptions}
      >
        <span>{!hasOptions ? "No Add-ons Available" : placeholder}</span>
        <i
          className={`fa-solid ${
            isOpen
              ? `fa-chevron-up ${CustomSelectAddOnsStyles.icon}`
              : `fa-chevron-down ${CustomSelectAddOnsStyles.icon}`
          }`}
          aria-hidden="true"
        />
      </button>
      {isOpen && hasOptions && (
        <ul
          id={listboxId}
          className={CustomSelectAddOnsStyles.optionList}
          role="listbox"
          aria-label="Add-ons options"
          tabIndex={-1}
        >
          {options.map((option: AddOnProps, index: number) => (
            <li
              key={option.addonId}
              ref={(el) => (optionRefs.current[index] = el)}
              id={`${listboxId}-option-${index}`}
              className={`${CustomSelectAddOnsStyles.optionItem} ${
                activeIndex === index ? CustomSelectAddOnsStyles.optionItemActive : ""
              }`}
              role="option"
              aria-selected={activeIndex === index}
              onClick={() => handleSelectOption(option)}
              onMouseEnter={() => setActiveIndex(index)}
            >
              {option.addonName}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CustomSelectAddOns;
